import type { Media } from './types';

export function bytes(n: number, digits = 1): string {
  if (!n || n < 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.min(Math.floor(Math.log(n) / Math.log(1000)), units.length - 1);
  const v = n / 1000 ** i;
  return `${v.toFixed(i === 0 ? 0 : v >= 100 ? 0 : digits)} ${units[i]}`;
}

export function clock(seconds: number): string {
  if (!isFinite(seconds) || seconds < 0) seconds = 0;
  const s = Math.floor(seconds % 60);
  const m = Math.floor((seconds / 60) % 60);
  const h = Math.floor(seconds / 3600);
  const mm = h ? String(m).padStart(2, '0') : String(m);
  return `${h ? `${h}:` : ''}${mm}:${String(s).padStart(2, '0')}`;
}

export function hours(h: number): string {
  if (h * 60 < 1) return `${Math.round(h * 3600)} s`;
  if (h < 1) return `${Math.round(h * 60)} min`;
  return `${h.toFixed(h < 10 ? 1 : 0)} h`;
}

export function ago(iso: string | number, lang: 'en' | 'ja' = 'en'): string {
  const t = typeof iso === 'number' ? iso : Date.parse(iso);
  const s = Math.max(0, (Date.now() - t) / 1000);
  if (lang === 'ja') {
    if (s < 60) return 'たった今';
    if (s < 3600) return `${Math.floor(s / 60)}分前`;
    if (s < 86400) return `${Math.floor(s / 3600)}時間前`;
    return `${Math.floor(s / 86400)}日前`;
  }
  if (s < 60) return 'just now';
  if (s < 3600) return `${Math.floor(s / 60)} min ago`;
  if (s < 86400) return `${Math.floor(s / 3600)} h ago`;
  return `${Math.floor(s / 86400)} d ago`;
}

/** Resolution class from width and height, so widescreen 1920×800 counts as 1080p (matches the server). */
export function classHeight(width: number, height: number): number {
  const c = Math.max(height, Math.round((width * 9) / 16));
  for (const std of [2160, 1440, 1080, 720, 480, 360]) if (c >= std * 0.92) return std;
  return c;
}

export function resolutionLabel(height: number, width = 0): string {
  const c = classHeight(width, height);
  return c === 2160 ? '4K' : `${c}p`;
}

const codecNames: Record<string, string> = { h264: 'H.264', hevc: 'HEVC', av1: 'AV1', vp9: 'VP9', mpeg4: 'MPEG-4', prores: 'ProRes' };

export function qualityLabel(m: Media): { main: string; sub: string } {
  if (!m.height) return { main: '—', sub: '' };
  const fps = m.fps > 31 ? ` ${Math.round(m.fps)}fps` : '';
  const codec = codecNames[m.videoCodec] ?? m.videoCodec?.toUpperCase() ?? '';
  const audio = m.channels > 2 ? ` · ${m.channels === 6 ? '5.1' : m.channels === 8 ? '7.1' : `${m.channels}ch`}` : '';
  return {
    main: `${resolutionLabel(m.height, m.width)}${fps}`,
    sub: `${codec}${m.bitDepth === 10 ? ' 10-bit' : ''}${m.hdr ? ' HDR' : ''}${audio}`,
  };
}

export function initials(name: string): string {
  const parts = name.trim().split(/\s+/);
  const first = [...(parts[0] ?? '?')][0] ?? '?';
  const second = parts.length > 1 ? [...parts[parts.length - 1]][0] : '';
  return (first + (second ?? '')).toUpperCase();
}

export function rttTone(rtt: number): 'green' | 'amber' | 'red' | 'gray' {
  if (!rtt) return 'gray';
  if (rtt < 90) return 'green';
  if (rtt < 220) return 'amber';
  return 'red';
}

export function greetingKey(): 'morning' | 'afternoon' | 'evening' {
  const h = new Date().getHours();
  if (h < 12) return 'morning';
  if (h < 18) return 'afternoon';
  return 'evening';
}

export const reducedMotion = () =>
  typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/** Skip intro animations for reduced-motion users and for pages opened in a background tab
 *  (rAF is paused there, so a tween would sit half-finished until the tab is shown). */
export const shouldAnimate = () => !reducedMotion() && document.visibilityState === 'visible';

/** "12 min", "1 h 05 min" — time left for a processing step. */
export function etaLabel(sec: number): string {
  if (!(sec > 0)) return '';
  if (sec < 60) return '<1 min';
  const m = Math.ceil(sec / 60);
  return m < 60 ? `${m} min` : `${Math.floor(m / 60)} h ${String(m % 60).padStart(2, '0')} min`;
}

/** A number input's value: a cleared or half-typed field reads as 0 (as `Number('')` did), never NaN. */
export const numberOrZero = (el: HTMLInputElement) => (Number.isNaN(el.valueAsNumber) ? 0 : el.valueAsNumber);
